import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Collapse from '@mui/material/Collapse';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Button from '@material-ui/core/Button';

const styles = {
  root: {
    flexGrow: 1,
    flexBasis: '35%',
    alignContent: 'center',
    display: 'flex',
  },
  collapse: {
    alignSelf: 'center',
    visibility: 'visible',
    flexGrow: 1,
  },
  overflowButton: {
    marginTop: 0,
    maxHeight: 32,
    maxWidth: 32,
  },
  collapseWrapper: {
    marginTop: -4,
    flexShrink: 1,
  },
};

/**
 * Creates an expandable section for long vertical elements. This component only shows the first `minHeight` of its
 * children, hiding the rest until the expand button is clicked, at which time it'll show the rest.
 * If the children are not at least `maxHeight` long enough, the expand button will not be shown, and the full content
 * will be displayed.
 * @param classes {object}
 * @param className {string}
 * @param children {ReactNode}
 * @param {number} [maxHeight=32] - Maximum height of content before its considered to overflow.
 * @param {number} [minHeight=16] - Minimum height of hidden content to display.
 * @returns {ReactNode}
 * @component
 * @example
 * // depending on font size, only part of the list will be shown
 * <Expandable>
 *     <ul>
 *         <li>foo</li>
 *         <li>bar</li>
 *         <li>baz</li>
 *     </ul>
 * </Expandable>
 */
const Expandable = ({
  classes, className, children, maxHeight, minHeight,
}) => {
  const overflowRef = useRef();
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const { current: node } = overflowRef;
    setHasOverflow(node.clientHeight > maxHeight);
  }, [children]);

  return (
    <div className={classNames(classes.root, className)}>
      <Collapse
        className={classes.collapse}
        classes={{
          wrapper: classes.collapseWrapper,
        }}
        in={expanded}
        style={{ minHeight }}
      >
        <div ref={overflowRef}>
          {children}
        </div>
      </Collapse>
      {hasOverflow
          && (
          <Button
            size="small"
            color="primary"
            onClick={() => setExpanded(!expanded)}
            variant="icon"
            className={classes.overflowButton}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
          )}
    </div>
  );
};

Expandable.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  /**
   * Maximum height of content before its considered to overflow.
   */
  maxHeight: PropTypes.number,
  /**
   * Minimum height of hidden content to display.
   */
  minHeight: PropTypes.number,
};

Expandable.defaultProps = {
  className: '',
  maxHeight: 32,
  minHeight: 16,
};

export default withStyles(styles)(Expandable);
